import { Editor } from '@tiptap/react';
import { useReducer } from 'react';
import { getExtensionOptions } from '../../../../../components/utils/tiptap';
import { DaGalleryAttrs, DaGalleryOptions } from '../../../gallery';
import {
  addMissingProtocol,
  validateHref,
} from '../../../../../components/utils/validateHref';

interface SettingsState
  extends Pick<
    DaGalleryAttrs,
    | 'alignment'
    | 'gridItemHeight'
    | 'gridItemWidth'
    | 'itemCropping'
    | 'layout'
    | 'layoutRowAlignment'
    | 'layoutSpacing'
    | 'linkOverrides'
    | 'torpedoRowHeight'
    | 'width'
  > {}

function settingsReducer(state: SettingsState, action): SettingsState {
  switch (action.type) {
    case 'setLayout':
      return { ...state, layout: action.layout };
    case 'setWidth':
      return { ...state, width: action.width };
    case 'setAlignment':
      return { ...state, alignment: action.alignment };
    case 'setGridItemWidth':
      return { ...state, gridItemWidth: action.width };
    case 'setGridItemHeight':
      return { ...state, gridItemHeight: action.height };
    case 'setLayoutSpacing':
      return { ...state, layoutSpacing: action.value };
    case 'setTorpedoRowHeight':
      return { ...state, torpedoRowHeight: action.value };
    case 'setItemCropping':
      return { ...state, itemCropping: action.value };
    case 'setRowAlignment':
      return { ...state, layoutRowAlignment: action.value };
    case 'updateLink':
      return {
        ...state,
        linkOverrides: {
          ...(state.linkOverrides ?? {}),
          [action.deviationId]: action.link,
        },
      };
    default:
      throw new Error();
  }
}

export default function useSettingsState(editor: Editor) {
  const attrs = editor.getAttributes('da-gallery');
  const options = getExtensionOptions<DaGalleryOptions>(editor, 'da-gallery');
  const initialState: SettingsState = {
    layout: attrs.layout,
    width: attrs.width ?? options.defaultWidth,
    alignment: attrs.alignment ?? 'center',
    gridItemWidth: attrs.gridItemWidth ?? options.defaultGridItemWidth,
    gridItemHeight: attrs.gridItemHeight ?? options.defaultGridItemHeight,
    itemCropping: attrs.itemCropping ?? 'crop',
    layoutRowAlignment: attrs.layoutRowAlignment ?? 'left',
    layoutSpacing: attrs.layoutSpacing ?? options.defaultLayoutSpacing,
    torpedoRowHeight: attrs.torpedoRowHeight ?? options.defaultTorpedoRowHeight,
    linkOverrides: attrs.linkOverrides,
  };

  const [state, dispatch] = useReducer(settingsReducer, initialState);

  const setGalleryLayout = e => {
    const layout = e.target.value;
    dispatch({ type: 'setLayout', layout });
    editor.commands.daGalleryChangeLayout(layout);
  };

  const setGalleryWidth = e => {
    const width = e;
    dispatch({ type: 'setWidth', width: +width });
    editor.commands.daGallerySetWidth(width);
  };

  const setGalleryAlignment = (selected, alignment) => {
    dispatch({ type: 'setAlignment', alignment });
    editor.commands.daGalleryAlign(alignment);
  };

  const setGridItemWidth = width => {
    dispatch({ type: 'setGridItemWidth', width });
    editor.commands.daGallerySetGridItemWidth(width);
  };

  const setGridItemHeight = height => {
    dispatch({ type: 'setGridItemHeight', height });
    editor.commands.daGallerySetGridItemHeight(height);
  };

  const setLayoutSpacing = value => {
    dispatch({ type: 'setLayoutSpacing', value });
    editor.commands.daGallerySetLayoutSpacing(value);
  };

  const setTorpedoRowHeight = value => {
    dispatch({ type: 'setTorpedoRowHeight', value });
    editor.commands.daGallerySetTorpedoRowHeight(value);
  };

  const setItemCropping = (selected, value) => {
    dispatch({ type: 'setItemCropping', value });
    editor.commands.daGallerySetItemCropping(value);
  };

  const setRowAlignment = (selected, value) => {
    dispatch({ type: 'setRowAlignment', value });
    editor.commands.daGallerySetRowAlignment(value);
  };

  const updateLinkFor = (deviationId, link) => {
    const action = { type: 'updateLink', deviationId, link };
    const newState = settingsReducer(state, action);
    dispatch(action);
    if (link === '' || validateHref(addMissingProtocol(link))) {
      editor.commands.daGallerySetLinkOverrides(newState.linkOverrides);
    }
  };

  return {
    state,
    setGalleryLayout,
    setGalleryWidth,
    setGalleryAlignment,
    setGridItemWidth,
    setGridItemHeight,
    setLayoutSpacing,
    setTorpedoRowHeight,
    setItemCropping,
    setRowAlignment,
    updateLinkFor,
  };
}
